/**
 * MapKitAutocompleteField — MUI Autocomplete backed by MapKit JS
 * `Search.autocomplete` with an `AddressFilter` constraint so the
 * dropdown only shows results of a given category (city, state,
 * country). Used to give the freeform Address Details fields proper
 * autocomplete instead of plain text inputs.
 *
 * Picking a result triggers a follow-up `Search.search(autocompleteResult)`
 * call to resolve the full `mapkit.Place` with structured address fields
 * (locality, administrativeArea, country). Apple's autocomplete results
 * carry only `displayLines` and `coordinate` — the structured fields
 * organizers actually want to cascade-fill ("picked Jackson Hole, WY →
 * also fill State=Wyoming, Country=United States") only exist on Place.
 *
 * Fires `onPick` with the resolved place parts. Parent decides which of
 * its own form fields to write to (City field cares about `locality`,
 * State cares about `administrativeArea`, etc.) and which neighbors to
 * cascade-fill.
 *
 * Free-text typing always works — `freeSolo` is on, so manual entry is
 * never gated by what MapKit knows.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Autocomplete as MuiAutocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import { ensureMapKitJsLoaded } from '../../lib/mapkit';

export type AddressCategory =
  | 'Country'
  | 'AdministrativeArea'
  | 'SubAdministrativeArea'
  | 'Locality'
  | 'SubLocality'
  | 'PostalCode';

/**
 * Structured address parts pulled from a resolved `mapkit.Place`. All
 * fields optional — Apple may or may not populate each part depending
 * on the location and locale.
 */
export interface ResolvedPlaceParts {
  /** Place.name (the venue/POI name). */
  name?: string;
  /** Place.fullThoroughfare (street line). */
  address?: string;
  /** Place.locality (city). */
  locality?: string;
  /** Place.administrativeArea (state/province). */
  administrativeArea?: string;
  /** Place.country (full country name). */
  country?: string;
}

export interface MapKitAutocompleteFieldProps {
  /** Field label rendered in the MUI TextField. */
  label: string;
  /** Current free-text value of the field. */
  value: string;
  /** Called as the user types (free-text edits). */
  onChange: (next: string) => void;
  /**
   * Called when the user picks a result from the dropdown AND the full
   * Place has been resolved via Search.search.
   *
   *   - `pickedText`: the user-visible dropdown value with any trailing
   *     ", administrativeArea" stripped (so picking "Jackson Hole, WY"
   *     yields "Jackson Hole"). This is what the picked field should
   *     display, preserving organizer expectation rather than Apple's
   *     authoritative `Place.locality` ("Jackson") which can surprise
   *     users when the colloquial name differs from the municipality.
   *   - `parts`: the structured Place fields (locality, administrative
   *     area, country, etc.) for cascade-filling neighbors.
   *
   * Not called when Search.search fails.
   */
  onPick?: (pickedText: string, parts: ResolvedPlaceParts) => void;
  /**
   * Address category to filter the autocomplete results by, via
   * `mapkit.AddressFilter.including`. Use 'Locality' for city, etc.
   */
  category: AddressCategory;
  /**
   * Optional coordinate hint for ranking (typically the event venue).
   * Used as both `coordinate` and `region` in the autocomplete options
   * so nearby cities outrank global ones.
   */
  biasCoordinate?: { lat: number; lng: number };
  /** Test ID suffix for the MUI input element. */
  testIdSuffix?: string;
  /** Optional helper text below the field. */
  helperText?: string;
}

interface DropOption {
  label: string;
  sublabel: string;
  raw: mapkit.SearchAutocompleteResult;
}

/**
 * Module-level shared mapkit.Search instance. Built once after MapKit JS
 * is loaded; multiple component instances reuse it.
 */
let sharedSearch: mapkit.Search | null = null;

async function ensureSearchReady(): Promise<boolean> {
  if (sharedSearch) return true;
  try {
    await ensureMapKitJsLoaded();
  } catch {
    return false;
  }
  if (typeof mapkit === 'undefined') return false;
  if (!sharedSearch) {
    sharedSearch = new mapkit.Search();
  }
  return true;
}

export function MapKitAutocompleteField({
  label,
  value,
  onChange,
  onPick,
  category,
  biasCoordinate,
  testIdSuffix,
  helperText,
}: MapKitAutocompleteFieldProps) {
  const [options, setOptions] = useState<DropOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Lazily bootstrap MapKit JS + initialize Search on mount. The shared
  // module-level singleton means multiple instances of this field on
  // one page only load + init once.
  useEffect(() => {
    let cancelled = false;
    ensureSearchReady().then((ok) => {
      if (!cancelled) setReady(ok);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const testId = useMemo(
    () => (testIdSuffix ? `mapkit-autocomplete-${testIdSuffix}` : 'mapkit-autocomplete'),
    [testIdSuffix],
  );

  const handleInput = useCallback(
    (_e: React.SyntheticEvent, next: string) => {
      onChange(next);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (!next.trim() || !ready || !sharedSearch) {
        setOptions([]);
        return;
      }
      setLoading(true);
      debounceRef.current = setTimeout(() => {
        const cb: mapkit.AutocompleteSearchCallback = (error, data) => {
          setLoading(false);
          if (error || !data?.results) {
            setOptions([]);
            return;
          }
          setOptions(
            data.results.slice(0, 8).map((result) => ({
              label: result.displayLines?.[0] ?? next,
              sublabel: result.displayLines?.[1] ?? '',
              raw: result,
            })),
          );
        };
        const opts: mapkit.SearchAutocompleteOptions = {
          includeQueries: false,
          includeAddresses: true,
          includePointsOfInterest: false,
        };
        // Apply category filter via AddressFilter when MapKit JS is recent
        // enough (5.78.1+). Older runtimes silently skip the filter and
        // return mixed results; users can still pick what they need.
        const mk = mapkit as unknown as { AddressFilter?: { including: (categories: AddressCategory[]) => unknown } };
        if (mk.AddressFilter) {
          (opts as unknown as { addressFilter: unknown }).addressFilter = mk.AddressFilter.including([category]);
        }
        if (biasCoordinate) {
          opts.coordinate = new mapkit.Coordinate(
            biasCoordinate.lat,
            biasCoordinate.lng,
          );
          opts.region = new mapkit.CoordinateRegion(
            new mapkit.Coordinate(biasCoordinate.lat, biasCoordinate.lng),
            new mapkit.CoordinateSpan(1.0, 1.0),
          );
        }
        sharedSearch!.autocomplete(next, cb, opts);
      }, 250);
    },
    [onChange, ready, category, biasCoordinate],
  );

  const handlePick = useCallback(
    (_e: React.SyntheticEvent, picked: DropOption | string | null) => {
      if (!picked || typeof picked === 'string') return;
      if (!sharedSearch) return;

      // The dropdown shows results like "Jackson Hole, WY" / "Wyoming, USA".
      // The picked field should display the leading segment (before the
      // first comma) — what the organizer saw and chose — not Apple's
      // authoritative structured field, which can be a different name
      // ("Jackson" for the Jackson Hole, WY locality).
      const pickedText = picked.label.split(',')[0]?.trim() || picked.label;

      const handleResolution = (place: mapkit.Place | null) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const p = place as any;
        const parts: ResolvedPlaceParts = {
          name: p?.name ?? undefined,
          address: p?.fullThoroughfare ?? undefined,
          locality: p?.locality ?? undefined,
          administrativeArea: p?.administrativeArea ?? undefined,
          country: p?.country ?? undefined,
        };
        onChange(pickedText);
        onPick?.(pickedText, parts);
        setOptions([]);
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const search = sharedSearch as any;
      try {
        search.search(
          picked.raw,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (err: unknown, data: any) => {
            if (err || !data?.places?.[0]) {
              // Resolution failed — at least put the picked text in the
              // input so the field isn't empty.
              onChange(pickedText);
              setOptions([]);
              return;
            }
            handleResolution(data.places[0]);
          },
        );
      } catch {
        onChange(pickedText);
        setOptions([]);
      }
    },
    [onChange, onPick, category],
  );

  return (
    <MuiAutocomplete<DropOption, false, false, true>
      freeSolo
      options={options}
      loading={loading}
      inputValue={value}
      onInputChange={handleInput}
      onChange={handlePick}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.label
      }
      filterOptions={(x) => x}
      isOptionEqualToValue={(a: DropOption, b: DropOption) =>
        a.label === b.label && a.sublabel === b.sublabel
      }
      renderOption={({ key, ...props }, option) => (
        <li key={key} {...props}>
          <Box>
            <Typography variant="body2">{option.label}</Typography>
            {option.sublabel && (
              <Typography variant="caption" color="text.secondary">
                {option.sublabel}
              </Typography>
            )}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          helperText={helperText}
          inputProps={{ ...params.inputProps, 'data-testid': testId }}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
